import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import {
  Plan,
  PlanBackupSummary,
  useGetPlanQuery,
} from "store/api/generatedApi";

import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useIsMobile } from "hooks/useIsMobile";
import { resolvedBackupStatuses, useBackups } from "hooks/useBackups";
import {
  deployPlanFormSlice,
  selectDeployPlanForm,
  selectDeployPlanFormCheckedCount,
  selectDeployPlanPreview,
} from "src/store/slices/deployPlanFormSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";

import {
  PlanId,
  usePlanId,
  useSetPlanId,
} from "components/common/boundary/PlanId";
import { useSearch } from "contexts/search/SearchContextProvider";
import { BackButton } from "./backButton";
import { CancelButton } from "./cancelButton";
import { CloseButton } from "./closeButton";
import { DeployForm } from "./deployForm";
import { Finished } from "./finished";
import { SendMessageButton } from "./sendMessageButton";
import { SyncWithLevel2Button } from "./syncWithLevel2Button";

import { useTenantData } from "contexts/tenant";
import { EditableDeployForm } from "./editableDeployForm";
import { MobileDeployForm } from "./mobileDeployForm";
import { MessageForm } from "./messageForm";
import { Periods } from "./Periods";

interface Props {
  open: boolean;
  doClose: () => void;
}

export const SyncAndSMSPlanModal = ({ open, doClose }: Props) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const planId = usePlanId();
  const setPlanId = useSetPlanId();
  const isMobile = useIsMobile();
  const preview = useAppSelector((state) =>
    selectDeployPlanPreview(state, planId)
  );
  const deployCount = useAppSelector((state) =>
    selectDeployPlanFormCheckedCount(state, planId)
  );

  const plan = useGetPlanQuery({ tenantName, planId });
  const backups = useBackups(planId);

  return (
    <Dialog
      open={open}
      onClose={doClose}
      fullWidth
      fullScreen={isMobile}
      sx={{
        "& .MuiDialog-paper": {
          height: isMobile ? "calc(100% - var(--app-bar-height, 0px))" : "90vh",
          maxWidth: isMobile ? "100%" : "unset",
          margin: isMobile ? 0 : undefined,
          marginTop: isMobile ? "var(--app-bar-height, 0px)" : undefined,
          borderRadius: isMobile ? 0 : undefined,
        },
      }}
    >
      <Stack
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          p: 2,
        }}
        direction="row"
      >
        <Stack spacing={2} direction={isMobile ? "column" : "row"}>
          <Stack direction="row" spacing={1}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ height: 32 }}>
              {t("deployPlan")}
            </Typography>
            <Chip label={deployCount} size="medium" />
          </Stack>
          {!isMobile && (
            <Periods
              periods={plan.data?.summary.period_summary ?? []}
              backups={backups.data?.backups ?? []}
              planId={planId}
            />
          )}
        </Stack>
        {preview && !isMobile ? (
          <Typography variant="h5">{preview.title}</Typography>
        ) : null}
      </Stack>
      <DataWrapper
        plan={plan}
        planId={planId}
        setPlanId={setPlanId}
        doClose={doClose}
        isMobile={isMobile}
      />
    </Dialog>
  );
};

interface WrapperProps {
  planId: PlanId;
  plan: {
    isSuccess: boolean;
    data?: Plan;
  };
  setPlanId: (planId: PlanId) => void;
  doClose: () => void;
  isMobile: boolean;
}

const DataWrapper = ({
  planId,
  plan,
  setPlanId,
  doClose,
  isMobile,
}: WrapperProps) => {
  const dispatch = useAppDispatch();
  const deployPlanForm = useAppSelector((state) =>
    selectDeployPlanForm(state, planId)
  );
  const backups = useBackups(planId);
  const { context } = useSearch();
  const { deploy_process } = useTenantData();
  const editablePreview = deploy_process === "edit_sync_and_sms";

  React.useEffect(() => {
    if (deployPlanForm === undefined) {
      dispatch(
        deployPlanFormSlice.actions.setDeployForm({
          planId,
        })
      );
    }
  }, [planId, deployPlanForm, dispatch]);

  if (
    context.status === "success" &&
    deployPlanForm !== undefined &&
    plan.isSuccess &&
    backups.isSuccess &&
    backups.data.backups.every((backup) =>
      resolvedBackupStatuses.includes(backup.status)
    )
  ) {
    switch (deployPlanForm.type) {
      case "deploy_form": {
        const allBackupPlanIds = backups.data.backups
          .filter(
            (
              backup: PlanBackupSummary
            ): backup is PlanBackupSummary & { plan_id: number } =>
              backup.plan_id !== null
          )
          .map(({ plan_id }) => plan_id as PlanId);

        if (isMobile) {
          return (
            <>
              <DialogContent sx={{ py: 0, px: 2 }}>
                <MobileDeployForm
                  preview={deployPlanForm.preview}
                  periods={plan.data?.summary.period_summary ?? []}
                  planId={planId}
                  setPlanId={setPlanId}
                  steelGrades={context.data.steelGrades.byIndex}
                  materials={context.data.materials.byIndex}
                />
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <CancelButton planId={planId} doClose={doClose} />
                <SyncWithLevel2Button
                  formPlanId={planId}
                  periods={plan.data?.summary.period_summary ?? []}
                  allBackupPlanIds={allBackupPlanIds}
                />
              </DialogActions>
            </>
          );
        }

        return (
          <>
            <DialogContent
              sx={{
                py: 0,
              }}
            >
              {editablePreview ? (
                <EditableDeployForm
                  preview={deployPlanForm.preview}
                  periods={plan.data?.summary.period_summary ?? []}
                  allBackupPlanIds={allBackupPlanIds}
                  planId={planId}
                  setPlanId={setPlanId}
                  materials={context.data.materials.byIndex}
                  steelGrades={context.data.steelGrades.byIndex}
                />
              ) : (
                <DeployForm
                  preview={deployPlanForm.preview}
                  periods={plan.data?.summary.period_summary ?? []}
                  planId={planId}
                  chefGroups={context.data.chemistryGroups.byIndex}
                  productGroups={context.data.productGroups.byIndex}
                  materials={context.data.materials.byIndex}
                />
              )}
            </DialogContent>
            <DialogActions>
              <CancelButton planId={planId} doClose={doClose} />
              <SyncWithLevel2Button
                formPlanId={planId}
                periods={plan.data?.summary.period_summary ?? []}
                allBackupPlanIds={allBackupPlanIds}
              />
            </DialogActions>
          </>
        );
      }
      case "message_form": {
        return (
          <>
            <DialogContent>
              <MessageForm
                periods={plan.data?.summary.period_summary ?? []}
                formPlanId={planId}
                backups={backups.data.backups}
              />
            </DialogContent>
            <DialogActions>
              <BackButton planId={planId} />
              <CancelButton planId={planId} doClose={doClose} />
              <SendMessageButton formPlanId={planId} />
            </DialogActions>
          </>
        );
      }
      case "finished_form": {
        return (
          <>
            <DialogContent>
              <Finished />
            </DialogContent>
            <DialogActions>
              <CloseButton formPlanId={planId} doClose={doClose} />
            </DialogActions>
          </>
        );
      }
    }
  }
  return (
    <DialogContent>
      <CircularProgress />
    </DialogContent>
  );
};
